import {
  ActionIcon,
  Alert,
  Button,
  Container,
  Group,
  Loader,
  Modal,
  Paper,
  Stack,
  Text,
  TextInput,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DataTable } from 'mantine-datatable';
import { AlertCircle, Pencil, Plus, Search, Trash2 } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import PageHeader from '@components/shared/PageHeader';
import { OPENAI_CONFIG } from '../api/translations/client';
import { translateToMultipleLanguages } from '../api/translations/translator';
import { TranslationActionsBar } from '../components/translations/TranslationActionsBar';
import { TranslationValueInput } from '../components/translations/TranslationValueInput';
import {
  applyTranslationKeyRemovalsForUpdate,
  getLang,
  TRANSLATION_API_LANG_CODES,
  TRANSLATION_LANG_CODES,
  updateLang,
  type LangResponse,
  type TranslationData,
  type TranslationLangCode,
} from '../services/translations/langManager';
import { parseTranslationAppIdParam, TRANSLATION_APP_LABELS } from '@types';
import {
  extractAllTags,
  restoreAllTags,
} from '../utils/translations/tagPlaceholders';
import {
  getNewTranslationKeyError,
  getNewTranslationValueError,
  sortTranslationKeys,
} from '../utils/translations/translationHelpers';
import { TranslationsApiErrorPanel } from './TranslationsApiErrorPanel';

const VALUE_PREVIEW_MAX = 120;

function getOpenAiTranslationErrorMessage(error: Error): string {
  const msg = error.message;
  if (
    !OPENAI_CONFIG.API_KEY ||
    msg.includes('API key is not configured') ||
    msg.includes('invalid_api_key') ||
    msg.includes('Incorrect API key') ||
    msg.includes('401') ||
    (msg.includes('API') && msg.includes('key'))
  ) {
    return 'Chiave API OpenAI non valida o non configurata. Imposta VITE_OPENAI_API_KEY in .env.local e riavvia il dev server.';
  }
  return msg;
}

const LANG_COLUMN_TITLE: Record<TranslationLangCode, string> = {
  it: 'IT',
  en: 'EN',
  pt: 'PT',
  fr: 'FR',
  es: 'ES',
  de: 'DE',
};

function previewValue(value: string): string {
  if (value.length <= VALUE_PREVIEW_MAX) return value;
  return `${value.slice(0, VALUE_PREVIEW_MAX)}…`;
}

export default function TranslationsAppDetail() {
  const { appId: appIdParam } = useParams<{ appId: string }>();
  const appId = useMemo(
    () => parseTranslationAppIdParam(appIdParam),
    [appIdParam]
  );

  const queryClient = useQueryClient();
  const [editedTranslations, setEditedTranslations] =
    useState<TranslationData | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [modalOpened, { open: openModal, close: closeModal }] =
    useDisclosure(false);
  const [addModalOpened, { open: openAddModal, close: closeAddModal }] =
    useDisclosure(false);
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [modalDraft, setModalDraft] = useState('');
  const [newKeyDraft, setNewKeyDraft] = useState('');
  const [newValueDraft, setNewValueDraft] = useState('');
  const [newKeyError, setNewKeyError] = useState<string | null>(null);
  const [newValueError, setNewValueError] = useState<string | null>(null);
  const [deleteKeyPending, setDeleteKeyPending] = useState<string | null>(null);

  useEffect(() => {
    if (appId === null) return;
    setEditedTranslations(null);
    setSearchQuery('');
    setEditingKey(null);
    setModalDraft('');
    closeModal();
    setNewKeyDraft('');
    setNewValueDraft('');
    setNewKeyError(null);
    setNewValueError(null);
    setDeleteKeyPending(null);
    closeAddModal();
    // Only appId should trigger reset when switching /app/1 ↔ /app/2
    // eslint-disable-next-line react-hooks/exhaustive-deps -- disclosure close fns not stable
  }, [appId]);

  const {
    data: langData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['lang', appId],
    queryFn: () => getLang(appId!),
    enabled: appId !== null,
  });

  const currentTranslations = useMemo(
    () => editedTranslations || langData?.data.it || {},
    [editedTranslations, langData?.data.it]
  );

  const existingItalianKeys = useMemo(
    () => new Set(Object.keys(currentTranslations)),
    [currentTranslations]
  );

  const filteredKeysOrdered = useMemo(() => {
    const keys = Object.keys(currentTranslations);
    const sorted = sortTranslationKeys(
      Object.fromEntries(keys.map(k => [k, currentTranslations[k] ?? '']))
    ).map(([k]) => k);

    if (!searchQuery) return sorted;

    const query = searchQuery.toLowerCase();
    const data = langData?.data;

    return sorted.filter(key => {
      if (key.toLowerCase().includes(query)) return true;

      const itDisplay = String(
        editedTranslations?.[key] ?? currentTranslations[key] ?? ''
      );
      if (itDisplay.toLowerCase().includes(query)) return true;

      if (!data) return false;

      return TRANSLATION_LANG_CODES.filter(code => code !== 'it').some(code => {
        const v = String(data[code][key] ?? '');
        return v.toLowerCase().includes(query);
      });
    });
  }, [currentTranslations, editedTranslations, langData?.data, searchQuery]);

  const tableRecords = useMemo(() => {
    const data = langData?.data;
    return filteredKeysOrdered.map(key => {
      const itVal = String(
        editedTranslations?.[key] ?? currentTranslations[key] ?? ''
      );
      const row: Record<string, string> & { key: string } = {
        key,
        it: itVal,
      };
      if (data) {
        for (const code of TRANSLATION_LANG_CODES) {
          if (code === 'it') continue;
          row[code] = String(data[code][key] ?? '');
        }
      } else {
        for (const code of TRANSLATION_LANG_CODES) {
          if (code === 'it') continue;
          row[code] = '';
        }
      }
      return row;
    });
  }, [
    filteredKeysOrdered,
    editedTranslations,
    currentTranslations,
    langData?.data,
  ]);

  const totalKeys = useMemo(
    () => Object.keys(currentTranslations).length,
    [currentTranslations]
  );

  const filteredKeys = useMemo(
    () => filteredKeysOrdered.length,
    [filteredKeysOrdered]
  );

  const hasChanges = useMemo(
    () => editedTranslations !== null,
    [editedTranslations]
  );

  const saveMutation = useMutation({
    mutationFn: async ({
      draftIt,
      server,
    }: {
      draftIt: TranslationData;
      server: LangResponse['data'];
    }) => {
      notifications.show({
        id: 'saving',
        loading: true,
        title: 'Salvataggio in corso...',
        message: 'Sto salvando le traduzioni italiane...',
        autoClose: false,
        withCloseButton: false,
      });

      try {
        const payload = applyTranslationKeyRemovalsForUpdate(
          {
            it: draftIt,
            en: {},
            pt: {},
            fr: {},
            es: {},
            de: {},
          },
          server
        );
        return await updateLang(payload, appId!);
      } finally {
        notifications.hide('saving');
      }
    },
    onSuccess: () => {
      notifications.show({
        title: 'Successo',
        message: 'Le traduzioni italiane sono state salvate!',
        color: 'green',
      });
      queryClient.invalidateQueries({ queryKey: ['lang', appId] });
      setEditedTranslations(null);
    },
    onError: (err: Error) => {
      notifications.show({
        title: 'Errore',
        message: `Errore durante il salvataggio: ${err.message}`,
        color: 'red',
      });
    },
  });

  const translateMutation = useMutation({
    mutationFn: async ({
      draftIt,
      server,
    }: {
      draftIt: TranslationData;
      server: LangResponse['data'];
    }) => {
      notifications.show({
        id: 'translating',
        loading: true,
        title: 'Traduzione in corso...',
        message:
          'Sto traducendo il testo italiano in tutte le lingue usando AI...',
        autoClose: false,
        withCloseButton: false,
      });

      try {
        if (!OPENAI_CONFIG.API_KEY) {
          throw new Error(
            'OpenAI API key is not configured. Please set VITE_OPENAI_API_KEY in your .env.local file.'
          );
        }

        const { cleanedTexts, tagsMap } = extractAllTags(draftIt);
        const targetLangs = TRANSLATION_API_LANG_CODES.filter(
          code => code !== 'it'
        );
        const translatedLanguages = await translateToMultipleLanguages(
          cleanedTexts,
          'it',
          [...targetLangs]
        );

        const allTranslations: Record<string, Record<string, string>> = {
          it: draftIt,
          ...translatedLanguages,
        };

        const translations = restoreAllTags(allTranslations, tagsMap);

        notifications.update({
          id: 'translating',
          loading: true,
          title: 'Salvataggio in corso...',
          message: 'Sto salvando le traduzioni sul server...',
        });

        const payload = applyTranslationKeyRemovalsForUpdate(
          translations,
          server
        );
        return await updateLang(payload, appId!);
      } finally {
        notifications.hide('translating');
      }
    },
    onSuccess: () => {
      notifications.show({
        title: 'Successo',
        message: 'Le traduzioni sono state salvate con successo!',
        color: 'green',
      });
      queryClient.invalidateQueries({ queryKey: ['lang', appId] });
      setEditedTranslations(null);
    },
    onError: (err: Error) => {
      notifications.show({
        title: 'Errore',
        message: `Errore durante il salvataggio: ${getOpenAiTranslationErrorMessage(err)}`,
        color: 'red',
      });
    },
  });

  const handleSaveOnly = useCallback(() => {
    if (!editedTranslations || appId === null || !langData?.data) return;
    saveMutation.mutate({
      draftIt: editedTranslations,
      server: langData.data,
    });
  }, [editedTranslations, saveMutation, appId, langData?.data]);

  const handleSaveAndTranslate = useCallback(() => {
    if (!editedTranslations || appId === null || !langData?.data) return;
    translateMutation.mutate({
      draftIt: editedTranslations,
      server: langData.data,
    });
  }, [editedTranslations, translateMutation, appId, langData?.data]);

  const handleReset = useCallback(() => {
    setEditedTranslations(null);
  }, []);

  const handleTranslationChange = useCallback(
    (key: string, value: string) => {
      setEditedTranslations(prev => {
        const current = prev || langData?.data?.it || {};
        return {
          ...current,
          [key]: value,
        };
      });
    },
    [langData?.data?.it]
  );

  const handleRemoveKey = useCallback(
    (key: string) => {
      if (editingKey === key) {
        closeModal();
        setEditingKey(null);
      }
      setEditedTranslations(prev => {
        const base = { ...(prev ?? langData?.data?.it ?? {}) };
        if (!(key in base)) return prev;
        const next = { ...base };
        delete next[key];
        return next;
      });
    },
    [langData?.data?.it, editingKey, closeModal]
  );

  const closeDeleteModal = useCallback(() => {
    setDeleteKeyPending(null);
  }, []);

  const confirmDeleteKey = useCallback(() => {
    if (deleteKeyPending === null) return;
    const key = deleteKeyPending;
    setDeleteKeyPending(null);
    handleRemoveKey(key);
  }, [deleteKeyPending, handleRemoveKey]);

  const openEditModal = useCallback(
    (key: string) => {
      const raw = editedTranslations?.[key] ?? currentTranslations[key] ?? '';
      setEditingKey(key);
      setModalDraft(typeof raw === 'string' ? raw : String(raw));
      openModal();
    },
    [editedTranslations, currentTranslations, openModal]
  );

  const applyModal = useCallback(() => {
    if (editingKey !== null) {
      handleTranslationChange(editingKey, modalDraft);
    }
    closeModal();
    setEditingKey(null);
  }, [editingKey, modalDraft, handleTranslationChange, closeModal]);

  const cancelModal = useCallback(() => {
    closeModal();
    setEditingKey(null);
  }, [closeModal]);

  const openAddKeyModal = useCallback(() => {
    setNewKeyDraft('');
    setNewValueDraft('');
    setNewKeyError(null);
    setNewValueError(null);
    openAddModal();
  }, [openAddModal]);

  const cancelAddModal = useCallback(() => {
    closeAddModal();
    setNewKeyDraft('');
    setNewValueDraft('');
    setNewKeyError(null);
    setNewValueError(null);
  }, [closeAddModal]);

  const applyAddKey = useCallback(() => {
    const keyErr = getNewTranslationKeyError(newKeyDraft, existingItalianKeys);
    if (keyErr !== null) {
      setNewKeyError(keyErr);
      setNewValueError(null);
      return;
    }
    const valueErr = getNewTranslationValueError(newValueDraft);
    if (valueErr !== null) {
      setNewKeyError(null);
      setNewValueError(valueErr);
      return;
    }
    const key = newKeyDraft.trim();
    handleTranslationChange(key, newValueDraft);
    cancelAddModal();
  }, [
    newKeyDraft,
    newValueDraft,
    existingItalianKeys,
    handleTranslationChange,
    cancelAddModal,
  ]);

  if (appId === null) {
    return <Navigate to='/admin/translations' replace />;
  }

  if (isLoading) {
    return (
      <Container size='2xl' py='lg'>
        <Stack align='center' justify='center' style={{ minHeight: '400px' }}>
          <Loader size='lg' />
          <Text>Caricamento traduzioni...</Text>
        </Stack>
      </Container>
    );
  }

  if (error) {
    return (
      <Container size='2xl' py='lg'>
        <Stack gap='lg'>
          <PageHeader
            title={TRANSLATION_APP_LABELS[appId]}
            subtitle='Impossibile caricare le traduzioni per questa app.'
            breadcrumbs={[
              { label: 'Admin', href: '/admin' },
              { label: 'Traduzioni', href: '/admin/translations' },
            ]}
            showBackButton
            backUrl='/admin/translations'
          />
          <TranslationsApiErrorPanel message={(error as Error).message} />
        </Stack>
      </Container>
    );
  }

  return (
    <Container size='2xl' py='lg'>
      <Stack gap='lg'>
        <PageHeader
          title={TRANSLATION_APP_LABELS[appId]}
          subtitle='Modifica le stringhe italiane in tabella; usa Salva o Salva e traduci dalla barra delle azioni sopra la tabella.'
          breadcrumbs={[
            { label: 'Admin', href: '/admin' },
            { label: 'Traduzioni', href: '/admin/translations' },
          ]}
          showBackButton
          backUrl='/admin/translations'
        />

        <Alert icon={<AlertCircle size={16} />} color='blue' variant='light'>
          <Text size='sm'>
            <strong>Importante:</strong> Modifica solo la traduzione italiana.
            Usa <strong>Salva e traduci</strong> per aggiornare inglese,
            portoghese, francese e spagnolo con l&apos;AI.
          </Text>
        </Alert>

        <TranslationActionsBar
          totalKeys={totalKeys}
          filteredKeys={filteredKeys}
          hasChanges={hasChanges}
          isPending={saveMutation.isPending}
          isPendingTranslate={translateMutation.isPending}
          onReset={handleReset}
          onSaveOnly={handleSaveOnly}
          onSaveAndTranslate={handleSaveAndTranslate}
        />

        <Group justify='space-between' align='flex-end' wrap='wrap' gap='sm'>
          <TextInput
            style={{ flex: 1, minWidth: 240 }}
            placeholder='Cerca per chiave o valore (qualsiasi lingua)...'
            leftSection={<Search size={16} />}
            value={searchQuery}
            onChange={e => setSearchQuery(e.currentTarget.value)}
            size='md'
          />
          <Button
            leftSection={<Plus size={16} />}
            onClick={openAddKeyModal}
            disabled={saveMutation.isPending || translateMutation.isPending}
          >
            Aggiungi chiave
          </Button>
        </Group>

        <Paper
          withBorder
          shadow='sm'
          radius='sm'
          p={0}
          style={{ overflow: 'hidden' }}
        >
          <div style={{ overflowX: 'auto' }}>
            <div style={{ minWidth: 960 }}>
              <DataTable
                minHeight={320}
                withTableBorder={false}
                borderRadius='sm'
                highlightOnHover
                verticalSpacing='sm'
                horizontalSpacing='md'
                withColumnBorders
                rowBackgroundColor={({ index }) =>
                  Number(index) % 2 === 0
                    ? undefined
                    : 'var(--mantine-color-gray-0)'
                }
                records={tableRecords}
                columns={[
                  {
                    accessor: 'key',
                    title: (
                      <Text size='xs' fw={600} c='dimmed'>
                        Chiave
                      </Text>
                    ),
                    width: 200,
                    render: ({ key }) => (
                      <Text size='sm' ff='monospace'>
                        {key}
                      </Text>
                    ),
                  },
                  ...TRANSLATION_LANG_CODES.map(code => ({
                    accessor: code,
                    title: (
                      <Text size='xs' fw={600} c='dimmed'>
                        {LANG_COLUMN_TITLE[code]}
                      </Text>
                    ),
                    width: 180,
                    render: (record: (typeof tableRecords)[number]) => (
                      <Text size='sm' style={{ whiteSpace: 'pre-wrap' }}>
                        {previewValue(String(record[code] ?? ''))}
                      </Text>
                    ),
                  })),
                  {
                    accessor: 'actions',
                    title: (
                      <Text size='xs' fw={600} c='dimmed' ta='right' w='100%'>
                        Azioni
                      </Text>
                    ),
                    width: 168,
                    textAlign: 'right' as const,
                    render: ({ key }: (typeof tableRecords)[number]) => (
                      <Group gap='xs' justify='flex-end' wrap='nowrap'>
                        <Button
                          size='compact-sm'
                          variant='light'
                          leftSection={<Pencil size={14} />}
                          onClick={() => openEditModal(key)}
                        >
                          Modifica
                        </Button>
                        <ActionIcon
                          variant='light'
                          color='red'
                          size='sm'
                          aria-label={`Elimina chiave ${key}`}
                          onClick={() => setDeleteKeyPending(key)}
                          disabled={
                            saveMutation.isPending ||
                            translateMutation.isPending
                          }
                        >
                          <Trash2 size={16} />
                        </ActionIcon>
                      </Group>
                    ),
                  },
                ]}
                idAccessor='key'
              />
            </div>
          </div>
        </Paper>

        {filteredKeys === 0 && searchQuery && (
          <Text ta='center' c='dimmed' py='xl'>
            Nessuna traduzione trovata per &quot;{searchQuery}&quot;
          </Text>
        )}

        <Modal
          opened={modalOpened}
          onClose={cancelModal}
          title={
            editingKey ? (
              <Stack gap={4}>
                <Text size='sm' c='dimmed'>
                  Chiave
                </Text>
                <Text ff='monospace' size='sm'>
                  {editingKey}
                </Text>
              </Stack>
            ) : (
              'Modifica traduzione'
            )
          }
          size='lg'
        >
          <Stack gap='md'>
            <Text size='sm' fw={500}>
              Valore (italiano)
            </Text>
            <TranslationValueInput
              value={modalDraft}
              onChange={setModalDraft}
            />
            <Group justify='flex-end'>
              <Button variant='default' onClick={cancelModal}>
                Annulla
              </Button>
              <Button onClick={applyModal}>Applica</Button>
            </Group>
            <Text size='xs' c='dimmed'>
              Le modifiche restano locali finché non usi Salva o Salva e traduci
              nella barra in alto.
            </Text>
          </Stack>
        </Modal>

        <Modal
          opened={addModalOpened}
          onClose={cancelAddModal}
          title='Aggiungi chiave'
          size='lg'
        >
          <Stack gap='md'>
            <TextInput
              label='Nome chiave'
              placeholder='es. my_feature_title'
              value={newKeyDraft}
              onChange={e => {
                setNewKeyDraft(e.currentTarget.value);
                setNewKeyError(null);
                setNewValueError(null);
              }}
              error={newKeyError}
              ff='monospace'
            />
            <Stack gap={4}>
              <Text size='sm' fw={500}>
                Valore (italiano)
              </Text>
              <TranslationValueInput
                value={newValueDraft}
                onChange={v => {
                  setNewValueDraft(v);
                  setNewValueError(null);
                }}
              />
              {newValueError !== null && (
                <Text size='sm' c='red'>
                  {newValueError}
                </Text>
              )}
              <Text size='xs' c='dimmed'>
                Serve almeno un testo italiano: con valore vuoto il salvataggio
                non crea la chiave sul server.
              </Text>
            </Stack>
            <Group justify='flex-end'>
              <Button variant='default' onClick={cancelAddModal}>
                Annulla
              </Button>
              <Button onClick={applyAddKey}>Aggiungi</Button>
            </Group>
          </Stack>
        </Modal>

        <Modal
          opened={deleteKeyPending !== null}
          onClose={closeDeleteModal}
          title='Elimina chiave'
          size='md'
        >
          <Stack gap='md'>
            <Text size='sm'>
              Rimuovere la chiave{' '}
              <Text span ff='monospace'>
                {deleteKeyPending ?? ''}
              </Text>{' '}
              dall&apos;insieme in modifica? Per applicare sul server usa Salva
              o Salva e traduci.
            </Text>
            <Group justify='flex-end'>
              <Button variant='default' onClick={closeDeleteModal}>
                Annulla
              </Button>
              <Button color='red' onClick={confirmDeleteKey}>
                Elimina
              </Button>
            </Group>
          </Stack>
        </Modal>
      </Stack>
    </Container>
  );
}
